import { useFeatureIsOn } from 'src/common/growthbook';
import { PRE_SIGNUP_STATE_STORAGE_KEY } from '../pre-signup-onboarding.slice';

/**
 * Custom hook to check if the delay-sign-up experiment is enabled.
 * This centralizes the feature flag check for the pre-signup onboarding flow.
 *
 * @returns boolean - true if the delay-sign-up experiment is enabled
 */
export const useDelaySignupExperiment = (): boolean => {
  return useFeatureIsOn('delay-sign-up');
};

/**
 * Custom hook that provides pre-signup context and helper functions.
 * Abstracts the pre-signup logic from components.
 */
export const usePreSignupContext = () => {
  const isDelaySignupEnabled = useDelaySignupExperiment();

  // We're in pre-signup context if the experiment is enabled and we have state in localStorage
  const isPreSignupContext = isDelaySignupEnabled && !!localStorage.getItem(PRE_SIGNUP_STATE_STORAGE_KEY);

  const updatePreSignupStateWithPath = (pathCode: string, firstRoomCode: string) => {
    try {
      const preSignupStateItem = localStorage.getItem(PRE_SIGNUP_STATE_STORAGE_KEY);
      if (preSignupStateItem) {
        const parsedState = JSON.parse(preSignupStateItem);
        if (parsedState?.state) {
          parsedState.state.selectedPath = pathCode;
          parsedState.state.redirectUrl = `/path-action/${pathCode}/join?redirect=/room/${firstRoomCode}?auto-join=true%26path=${pathCode}`;
          localStorage.setItem(PRE_SIGNUP_STATE_STORAGE_KEY, JSON.stringify(parsedState));
        }
      }
    } catch (error) {
      console.error('Failed to update pre-signup state with redirect URL:', error);
    }
  };

  const updatePreSignupStateWithSelectedPath = (pathCode: string) => {
    try {
      const preSignupStateItem = localStorage.getItem(PRE_SIGNUP_STATE_STORAGE_KEY);
      if (preSignupStateItem) {
        const parsedState = JSON.parse(preSignupStateItem);
        if (parsedState?.state) {
          parsedState.state.selectedPath = pathCode;
          localStorage.setItem(PRE_SIGNUP_STATE_STORAGE_KEY, JSON.stringify(parsedState));
        }
      }
    } catch (error) {
      console.error('Failed to update pre-signup state with selected path:', error);
    }
  };

  return {
    isPreSignupContext,
    updatePreSignupStateWithPath,
    updatePreSignupStateWithSelectedPath,
  };
};
